import React, { useState, useEffect } from 'react';
import { Modal, Input, Form, Tree, Button, Dropdown, Space, message } from 'antd';
import type { MenuProps } from 'antd';
import type { DataNode } from 'antd/es/tree';
import {
  FolderOutlined,
  FolderAddOutlined,
  EditOutlined,
  DeleteOutlined,
  MoreOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// Type definitions
interface SaveDialogProps {
  open: boolean;
  onCancel: () => void;
  onSave: (filename: string) => Promise<void>;
  initialName?: string;
}

interface FolderNode extends DataNode {
  title: string;
  key: string;
  children?: FolderNode[];
}

interface FormValues {
  filename: string;
}

const SaveDialog: React.FC<SaveDialogProps> = ({ open, onCancel, onSave, initialName = '' }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const [saving, setSaving] = useState<boolean>(false);
  const [folderStructure, setFolderStructure] = useState<FolderNode[]>([]);
  const [selectedFolder, setSelectedFolder] = useState<string>('/');
  const [newFolderModalVisible, setNewFolderModalVisible] = useState<boolean>(false);
  const [newFolderName, setNewFolderName] = useState<string>('');

  // Load folder structure from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem('folderStructure');
    if (stored) {
      setFolderStructure(JSON.parse(stored) as FolderNode[]);
    } else {
      // Initialize with root folder
      const initial: FolderNode[] = [
        {
          title: 'Root',
          key: '/',
          children: [],
        },
      ];
      setFolderStructure(initial);
      localStorage.setItem('folderStructure', JSON.stringify(initial));
    }
  }, []);

  const saveFolderStructure = (newStructure: FolderNode[]): void => {
    setFolderStructure(newStructure);
    localStorage.setItem('folderStructure', JSON.stringify(newStructure));
  };

  const handleAddFolder = (): void => {
    if (!newFolderName) return;

    const updateTree = (nodes: FolderNode[], parentKey: string): FolderNode[] => {
      return nodes.map((node) => {
        if (node.key === parentKey) {
          return {
            ...node,
            children: [
              ...(node.children || []),
              {
                title: newFolderName,
                key: `${parentKey}${newFolderName}/`,
                children: [],
              },
            ],
          };
        }
        if (node.children) {
          return {
            ...node,
            children: updateTree(node.children, parentKey),
          };
        }
        return node;
      });
    };

    const newStructure = updateTree(folderStructure, selectedFolder);
    saveFolderStructure(newStructure);
    setNewFolderModalVisible(false);
    setNewFolderName('');
  };

  const handleDeleteFolder = (key: string): void => {
    const removeFolder = (nodes: FolderNode[], targetKey: string): FolderNode[] => {
      return nodes.filter((node) => {
        if (node.key === targetKey) return false;
        if (node.children) {
          node.children = removeFolder(node.children, targetKey);
        }
        return true;
      });
    };

    const newStructure = removeFolder(folderStructure, key);
    saveFolderStructure(newStructure);
  };

  const handleRenameFolder = (key: string, newName: string): void => {
    const renameInTree = (nodes: FolderNode[], targetKey: string, newTitle: string): FolderNode[] => {
      return nodes.map((node) => {
        if (node.key === targetKey) {
          const newKey = targetKey.replace(node.title, newTitle);
          return { ...node, title: newTitle, key: newKey };
        }
        if (node.children) {
          return {
            ...node,
            children: renameInTree(node.children, targetKey, newTitle),
          };
        }
        return node;
      });
    };

    const newStructure = renameInTree(folderStructure, key, newName);
    saveFolderStructure(newStructure);
  };

  const handleOk = async (): Promise<void> => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await onSave(`${selectedFolder}${values.filename}`);
      form.resetFields();
      onCancel();
    } catch (error) {
      message.error(t('saveDialog.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const folderContextMenu = (nodeKey: string): MenuProps['items'] => [
    {
      key: 'rename',
      icon: <EditOutlined />,
      label: t('saveDialog.rename'),
      onClick: () => {
        const newName = window.prompt(t('saveDialog.enterFolderName'));
        if (newName) handleRenameFolder(nodeKey, newName);
      },
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: t('saveDialog.delete'),
      onClick: () => handleDeleteFolder(nodeKey),
    },
  ];

  return (
    <>
      <Modal
        title={t('saveDialog.title')}
        open={open}
        onCancel={onCancel}
        onOk={handleOk}
        okText={t('saveDialog.save')}
        confirmLoading={saving}
        width={600}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div style={{ marginBottom: 16 }}>
            <Button icon={<FolderAddOutlined />} onClick={() => setNewFolderModalVisible(true)}>
              {t('saveDialog.newFolder')}
            </Button>
          </div>

          <Tree
            treeData={folderStructure}
            selectedKeys={[selectedFolder]}
            onSelect={(keys) => setSelectedFolder((keys[0] as string) || '/')}
            titleRender={(node) => (
              <Space>
                <FolderOutlined />
                {node.title}
                <Dropdown menu={{ items: folderContextMenu(node.key as string) }} trigger={['click']}>
                  <MoreOutlined />
                </Dropdown>
              </Space>
            )}
          />

          <Form form={form} layout="vertical" initialValues={{ filename: initialName }}>
            <Form.Item
              name="filename"
              label={t('saveDialog.pipelineName')}
              rules={[
                { required: true, message: t('saveDialog.pipelineNameRequired') },
                {
                  pattern: /^[a-zA-Z0-9-_]+$/,
                  message: t('saveDialog.invalidName'),
                },
              ]}
            >
              <Input placeholder={t('saveDialog.placeholder')} addonBefore={selectedFolder} />
            </Form.Item>
          </Form>
        </Space>
      </Modal>

      <Modal
        title={t('saveDialog.newFolder')}
        open={newFolderModalVisible}
        onCancel={() => setNewFolderModalVisible(false)}
        onOk={handleAddFolder}
      >
        <Input
          placeholder={t('saveDialog.enterFolderNamePlaceholder')}
          value={newFolderName}
          onChange={(e) => setNewFolderName(e.target.value)}
        />
      </Modal>
    </>
  );
};

export default SaveDialog;
